<div class="card push-notifications-card">
  <div class="push-notifications-header">
    <div>
      <h2>Push notifications</h2>
      <p>
        Send an announcement to the mobile app. Exercise and workout
        notifications are sent automatically when they are pushed to a location.
      </p>
    </div>
    <span class="admin-badge">Superadmin only</span>
  </div>

  <form [formGroup]="notificationForm" (ngSubmit)="submit()">
    <mat-form-field appearance="outline" class="w-100">
      <mat-label>Title</mat-label>
      <input
        matInput
        formControlName="title"
        [maxlength]="titleMaxLength"
        placeholder="New features are live"
      />
      <mat-hint align="end">{{ titleLength }} / {{ titleMaxLength }}</mat-hint>
      <mat-error *ngIf="notificationForm.get('title').hasError('required')">
        A title is required.
      </mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-100">
      <mat-label>Message</mat-label>
      <textarea
        matInput
        rows="4"
        formControlName="body"
        [maxlength]="bodyMaxLength"
        placeholder="Tell users what changed in the app."
      ></textarea>
      <mat-hint align="end">{{ bodyLength }} / {{ bodyMaxLength }}</mat-hint>
      <mat-error *ngIf="notificationForm.get('body').hasError('required')">
        A message is required.
      </mat-error>
    </mat-form-field>

    <div class="audience-section">
      <span class="section-label">Audience</span>
      <mat-radio-group
        formControlName="audience"
        class="audience-options"
        (change)="onAudienceChanged()"
      >
        <mat-radio-button [value]="audiences.AllUsers">
          All users
          <small>Every registered device across every location.</small>
        </mat-radio-button>
        <mat-radio-button [value]="audiences.SelectedLocations">
          Selected locations
          <small>
            Everyone with access to the locations you pick, on every device they
            are signed in on.
          </small>
        </mat-radio-button>
        <mat-radio-button [value]="audiences.SelectedUsers">
          Specific users
          <small>
            Only the people you pick, on every device they are signed in on.
          </small>
        </mat-radio-button>
      </mat-radio-group>
    </div>

    <div class="location-picker" *ngIf="isLocationAudience">
      <div class="location-picker-heading">
        <span class="section-label">Locations</span>
        <span class="selection-count">
          {{ selectedLocations.length }} selected
        </span>
      </div>
      <app-organizations-with-locations-form
        [showActions]="false"
        [requireActive]="true"
        (_locationPressed)="onLocationPressed($event)"
      ></app-organizations-with-locations-form>
    </div>

    <div class="user-picker" *ngIf="isUserAudience">
      <div class="picker-heading">
        <span class="section-label">Users</span>
        <span class="selection-count">
          {{ selectedUsers.length }} selected
        </span>
      </div>

      <div class="selected-users" *ngIf="selectedUsers.length">
        <button
          type="button"
          class="user-chip"
          *ngFor="let user of selectedUsers"
          (click)="removeUser(user)"
          [matTooltip]="user.email"
        >
          <span>{{ displayName(user) }}</span>
          <mat-icon>close</mat-icon>
        </button>
        <button type="button" mat-button color="primary" (click)="clearSelectedUsers()">
          Clear
        </button>
      </div>

      <p class="picker-warning" *ngIf="selectedUsersWithoutDevices">
        <mat-icon>info</mat-icon>
        {{ selectedUsersWithoutDevices }} selected
        {{ selectedUsersWithoutDevices === 1 ? "user has" : "users have" }} no
        registered device — they will only see this in the app.
      </p>

      <mat-form-field appearance="outline" class="w-100 recipient-search">
        <mat-label>Search users</mat-label>
        <input
          matInput
          [value]="recipientSearch"
          (input)="searchRecipients($any($event.target).value)"
          placeholder="Name, username, email or organization"
        />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <div class="loading-state small" *ngIf="isRecipientsLoading">
        <mat-spinner diameter="28"></mat-spinner>
      </div>

      <ul class="recipient-list" *ngIf="!isRecipientsLoading && recipients.length">
        <li *ngFor="let user of recipients" [class.selected]="isUserSelected(user)">
          <mat-checkbox
            [checked]="isUserSelected(user)"
            (change)="toggleUser(user)"
          >
            <span class="recipient-name">{{ displayName(user) }}</span>
            <span class="recipient-meta">
              {{ user.email }} · {{ user.organizationName }}
            </span>
            <span class="recipient-meta" *ngIf="user.locationNames.length">
              {{ user.locationNames.join(", ") }}
            </span>
            <span class="recipient-meta no-devices" *ngIf="!user.deviceCount">
              No registered device
            </span>
          </mat-checkbox>
        </li>
      </ul>

      <div
        class="empty-state small"
        *ngIf="!isRecipientsLoading && !recipients.length"
      >
        No users match this search.
      </div>

      <div
        class="history-pagination"
        *ngIf="!isRecipientsLoading && recipients.length"
      >
        <button
          type="button"
          mat-button
          [disabled]="recipientPage <= 1"
          (click)="changeRecipientPage(-1)"
        >
          Previous
        </button>
        <span>Page {{ recipientPage }} of {{ totalRecipientPages }}</span>
        <button
          type="button"
          mat-button
          [disabled]="recipientPage >= totalRecipientPages"
          (click)="changeRecipientPage(1)"
        >
          Next
        </button>
      </div>
    </div>

    <div class="actions">
      <button
        type="button"
        mat-stroked-button
        color="primary"
        [disabled]="isSending"
        (click)="sendTest()"
      >
        <mat-icon>phonelink_ring</mat-icon>
        Send to my devices
      </button>

      <button
        type="submit"
        mat-flat-button
        color="primary"
        class="send-button"
        [disabled]="isSending"
      >
        <span class="button-content">
          <span class="button-icon-slot">
            <mat-spinner *ngIf="isSending" diameter="18"></mat-spinner>
            <mat-icon *ngIf="!isSending">send</mat-icon>
          </span>
          <span>Send notification</span>
          <span class="button-icon-slot" aria-hidden="true"></span>
        </span>
      </button>
    </div>
  </form>

  <section class="history-section">
    <div class="history-heading">
      <div>
        <h3>Recent notifications</h3>
        <p>Automatic and manual sends, newest first.</p>
      </div>
      <button
        type="button"
        mat-icon-button
        [disabled]="isHistoryLoading"
        (click)="loadHistory()"
        aria-label="Refresh notification history"
      >
        <mat-icon>refresh</mat-icon>
      </button>
    </div>

    <div class="loading-state" *ngIf="isHistoryLoading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>

    <div class="history-table-wrapper" *ngIf="!isHistoryLoading && history.length">
      <table class="history-table">
        <thead>
          <tr>
            <th>Sent</th>
            <th>Type</th>
            <th>Title</th>
            <th>Audience</th>
            <th class="numeric">Devices</th>
            <th class="numeric">Delivered</th>
            <th class="numeric">Failed</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of history">
            <td>{{ item.createdAt | date: "short" }}</td>
            <td>
              <span class="type-chip">{{ item.type }}</span>
            </td>
            <td>
              <strong>{{ item.title }}</strong>
              <small>{{ item.body }}</small>
            </td>
            <td>{{ item.audienceDescription }}</td>
            <td class="numeric">{{ item.deviceCount }}</td>
            <td class="numeric success">{{ item.successCount }}</td>
            <td
              class="numeric"
              [class.failure]="item.failureCount > 0"
              [matTooltip]="item.errorSummary"
              [matTooltipDisabled]="!item.errorSummary"
            >
              {{ item.failureCount }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="history-pagination" *ngIf="!isHistoryLoading && history.length">
      <button
        type="button"
        mat-button
        [disabled]="historyPage <= 1"
        (click)="changeHistoryPage(-1)"
      >
        Previous
      </button>
      <span>Page {{ historyPage }} of {{ totalHistoryPages }}</span>
      <button
        type="button"
        mat-button
        [disabled]="historyPage >= totalHistoryPages"
        (click)="changeHistoryPage(1)"
      >
        Next
      </button>
    </div>

    <div class="empty-state" *ngIf="!isHistoryLoading && !history.length">
      No notifications have been sent yet.
    </div>
  </section>
</div>
